import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  c514Api,
  AbnormalPendingItem,
  PendingResponse,
  ApproveResponse,
  ApprovalFilter,
  FC120Detail,
} from '../../../services/c514Api';

export interface UseC514Return {
  // State
  loading: boolean;
  approving: boolean;
  pendingItems: AbnormalPendingItem[];
  total: number;
  selectedKeys: string[];
  filter: ApprovalFilter;
  detailData: FC120Detail | null;
  detailLoading: boolean;
  detailVisible: boolean;

  // Actions
  setFilter: (f: ApprovalFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchPending: () => Promise<void>;
  batchApprove: () => Promise<ApproveResponse | null>;
  loadDetail: (doNo: string) => Promise<void>;
  closeDetail: () => void;
}

export function useC514(): UseC514Return {
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pendingItems, setPendingItems] = useState<AbnormalPendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<ApprovalFilter>({ doStart: '', doEnd: '' });
  const [detailData, setDetailData] = useState<FC120Detail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: PendingResponse = await c514Api.getPending(filter);
      setPendingItems(resp.items || []);
      setTotal(resp.total || 0);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadDetail = useCallback(async (doNo: string) => {
    setDetailLoading(true);
    setDetailVisible(true);
    try {
      const data = await c514Api.getDetail(doNo);
      setDetailData(data);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '取得明細失敗');
      message.error(msg);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetailVisible(false);
    setDetailData(null);
  }, []);

  const batchApprove = useCallback(async (): Promise<ApproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆異常交運單進行核准');
      return null;
    }
    setApproving(true);
    try {
      const resp = await c514Api.approve(selectedKeys);
      if (resp.failed > 0) {
        message.warning(`核准完成：成功 ${resp.success} 筆，失敗 ${resp.failed} 筆`);
      } else {
        message.success(resp.message || `核准完成！共 ${resp.success} 筆`);
      }
      await fetchPending();
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
      message.error(msg);
      return null;
    } finally {
      setApproving(false);
    }
  }, [selectedKeys, fetchPending]);

  return {
    loading,
    approving,
    pendingItems,
    total,
    selectedKeys,
    filter,
    detailData,
    detailLoading,
    detailVisible,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
    loadDetail,
    closeDetail,
  };
}
